import { useId, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
  Label,
} from '@monksflow/monks-ui';
import type { WorkspacesFieldProps, WorkspaceOption } from './WorkspacesField.types';

/**
 * Built on the combobox primitives rather than `shared/ui/TagInput`, which wraps the same
 * component. The picker needs one chip that carries no remove control and one option that cannot
 * be toggled (the creator's own workspace), and the wrapper renders every chip and option the
 * same way. Reusing it would mean adding per-item overrides there for a rule only this field has.
 * Shared by every resource that can be shared with a chosen set of workspaces; the helper copy
 * names the resource, so the caller supplies it.
 */
export function WorkspacesField(props: WorkspacesFieldProps) {
  const { options, value, onChange, disabled, error, helperText, lockedWorkspaceId } = props;
  const fieldId = useId();
  const { t } = useTranslation('common');

  // Anchor the popup on the full-width chips row rather than the inner input, which shrinks as
  // chips fill in. Without an anchor the popup has nothing to position against.
  const anchorRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(
    () => options.filter((workspace) => value.includes(workspace.id)),
    [options, value],
  );

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border p-4">
      <div className="flex items-baseline justify-between gap-4">
        <Label htmlFor={fieldId}>
          {t('components.workspacesField.workspaces')}
          <span className="text-destructive" aria-hidden="true">
            *
          </span>
        </Label>
        <span className="text-sm text-muted-foreground">
          {t('components.workspacesField.countSelected', {
            count: selected.length,
            total: options.length,
          })}
        </span>
      </div>

      <Combobox<WorkspaceOption, true>
        multiple
        items={options}
        value={selected}
        onValueChange={(next) => {
          const ids = next.map((workspace) => workspace.id);
          onChange(
            lockedWorkspaceId && !ids.includes(lockedWorkspaceId)
              ? [lockedWorkspaceId, ...ids]
              : ids,
          );
        }}
        disabled={disabled}
        itemToStringLabel={(workspace) => workspace.name}
        itemToStringValue={(workspace) => workspace.id}
        isItemEqualToValue={(a, b) => a.id === b.id}
      >
        <ComboboxChips ref={anchorRef} className={error ? 'border-destructive' : undefined}>
          <ComboboxValue>
            {(workspaces: WorkspaceOption[]) =>
              workspaces.map((workspace) => (
                <ComboboxChip key={workspace.id} showRemove={workspace.id !== lockedWorkspaceId}>
                  {workspace.name}
                </ComboboxChip>
              ))
            }
          </ComboboxValue>
          <ComboboxChipsInput
            id={fieldId}
            placeholder={
              selected.length === 0 ? t('components.workspacesField.selectWorkspaces') : ''
            }
          />
          {/* A real trigger, not a decorative chevron: this is what makes the arrow open the list. */}
          <ComboboxTrigger
            className="ml-auto"
            aria-label={t('components.workspacesField.workspaces')}
          />
        </ComboboxChips>

        {/* `pointer-events-auto`: the popup portals to document.body, and a Radix Dialog with
            `modal` sets `pointer-events: none` there, granting it back only to the overlay and
            its content. Without this the list opens and nothing in it can be clicked. */}
        {/* The same portal also leaves the list outside the dialog's scroll lock, which swallows
            wheel and touch movement anywhere but inside the dialog and leaves a long list stuck.
            Stopping them here keeps them from ever reaching that lock, so the list scrolls itself. */}
        <ComboboxContent
          anchor={anchorRef}
          className="pointer-events-auto"
          onWheel={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
        >
          <ComboboxList>
            <ComboboxCollection>
              {(workspace: WorkspaceOption) => (
                <ComboboxItem
                  key={workspace.id}
                  value={workspace}
                  disabled={workspace.id === lockedWorkspaceId}
                >
                  {workspace.name}
                </ComboboxItem>
              )}
            </ComboboxCollection>
            <ComboboxEmpty>{t('components.workspacesField.noWorkspacesFound')}</ComboboxEmpty>
          </ComboboxList>
        </ComboboxContent>
      </Combobox>

      <p className={error ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
        {error ?? helperText}
      </p>
    </div>
  );
}
